import type { VaultExitType } from '@/app/api/t/constants'
import { classifyTransactionErrorFrom } from '@/lib/observability/client/trackers/transaction/error-classifier'
import type { WalletType } from '@/lib/observability/shared/wallet-type'

import { getCheckoutTrackingProperties } from './checkout-funnel'
import type { CheckoutAttemptTrackingContext } from './checkout-funnel'
import { trackAnalyticsEvent } from '../track'

interface TransactionInitiatedParams {
  chainId: number
  chainName: string
  operationTypes: string[]
  isSimpleTx: boolean
  walletType: WalletType
  exitType?: VaultExitType
}

interface TransactionSignedParams {
  txHash: string
  chainId: number
  chainName: string
  walletType: WalletType
  transactionCount: number
  simulationBypassed: boolean
  checkoutTracking?: CheckoutAttemptTrackingContext
  exitType?: VaultExitType
}

interface TransactionConfirmedParams {
  txHash: string
  chainId: number
  chainName: string
  blockNumber: string
  operationTypes: string[]
  walletType: WalletType
  exitType?: VaultExitType
}

interface TransactionFailedParams {
  txHash: string | undefined
  chainId: number
  chainName: string
  operationTypes: string[]
  walletType: WalletType
  error: unknown
  exitType?: VaultExitType
}

export function trackTransactionInitiated(params: TransactionInitiatedParams) {
  trackAnalyticsEvent('transaction_initiated', {
    chain_id: params.chainId,
    chain_name: params.chainName,
    operation_types: params.operationTypes,
    operation_count: params.operationTypes.length,
    is_simple_tx: params.isSimpleTx,
    wallet_type: params.walletType,
    exit_type: params.exitType
  })
}

export function trackTransactionSigned(params: TransactionSignedParams) {
  trackAnalyticsEvent('transaction_signed', {
    tx_hash: params.txHash,
    chain_id: params.chainId,
    chain_name: params.chainName,
    wallet_type: params.walletType,
    transaction_count: params.transactionCount,
    simulation_bypassed: params.simulationBypassed,
    exit_type: params.exitType,
    ...(params.checkoutTracking ? getCheckoutTrackingProperties(params.checkoutTracking) : {})
  })
}

export function trackTransactionConfirmed(params: TransactionConfirmedParams) {
  trackAnalyticsEvent('transaction_confirmed', {
    tx_hash: params.txHash,
    chain_id: params.chainId,
    chain_name: params.chainName,
    block_number: params.blockNumber,
    operation_types: params.operationTypes,
    wallet_type: params.walletType,
    exit_type: params.exitType
  })
}

export function trackTransactionFailed(params: TransactionFailedParams) {
  const errorCategory = classifyTransactionErrorFrom(params.error)

  trackAnalyticsEvent('transaction_failed', {
    tx_hash: params.txHash,
    chain_id: params.chainId,
    chain_name: params.chainName,
    operation_types: params.operationTypes,
    wallet_type: params.walletType,
    error_category: errorCategory,
    exit_type: params.exitType
  })
}
